import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { clinicInfo } from '@/mocks/homeData';

export default function Contato() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9ggui4ugrdi4rjg5830', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (res.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.message
          || (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.detail
          || (parsed as { message?: string })?.message
          || responseText;
        setFormError(typeof serverMsg === 'string' ? serverMsg : 'Erro ao enviar mensagem. Tente novamente.');
        setFormStatus('error');
      }
    } catch {
      setFormError('Erro de conexão. Verifique sua internet e tente novamente.');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-100 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-400 via-transparent to-transparent" />
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label"
            >
              Fale Conosco
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 mt-4 leading-[1.05]"
            >
              Entre em<br />
              <span className="italic font-light">Contato.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-[680px] mx-auto mt-6 text-foreground-600 text-base leading-relaxed"
            >
              Estamos prontas para recebê-la. Entre em contato para agendar sua consulta ou tirar dúvidas sobre nossos serviços.
            </motion.p>
          </div>
        </section>

        {/* Contact Content */}
        <section className="relative bg-background-50 py-24 md:py-32 lg:py-40">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
              {/* Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <h2 className="font-heading text-3xl text-foreground-950 mb-2">Envie uma mensagem</h2>
                <p className="text-sm text-foreground-600 mb-8">Responderemos em até 24 horas úteis.</p>

                <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="nome" className="block text-xs tracking-[0.06em] uppercase text-foreground-500 font-label mb-1.5">Nome *</label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        required
                        className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-colors"
                        placeholder="Seu nome completo"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs tracking-[0.06em] uppercase text-foreground-500 font-label mb-1.5">E-mail *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-colors"
                        placeholder="seu@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="telefone" className="block text-xs tracking-[0.06em] uppercase text-foreground-500 font-label mb-1.5">Telefone</label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-colors"
                      placeholder="(85) 99999-9999"
                    />
                  </div>

                  <div>
                    <label htmlFor="assunto" className="block text-xs tracking-[0.06em] uppercase text-foreground-500 font-label mb-1.5">Assunto *</label>
                    <select
                      id="assunto"
                      name="assunto"
                      required
                      className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-colors"
                    >
                      <option value="">Selecione um assunto</option>
                      <option value="Agendamento">Agendamento de Consulta</option>
                      <option value="Dúvidas">Dúvidas sobre Serviços</option>
                      <option value="Orçamento">Solicitar Orçamento</option>
                      <option value="Retorno">Retorno de Exame</option>
                      <option value="Outro">Outro Assunto</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="mensagem" className="block text-xs tracking-[0.06em] uppercase text-foreground-500 font-label mb-1.5">Mensagem *</label>
                    <textarea
                      id="mensagem"
                      name="mensagem"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="Como podemos ajudar você?"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 font-label">Máximo de 500 caracteres</p>
                  </div>

                  {formStatus === 'error' && (
                    <div className="p-4 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  {formStatus === 'success' && (
                    <div className="p-4 rounded-md bg-green-50 border border-green-200 text-sm text-green-700">
                      Mensagem enviada com sucesso! Entraremos em contato em breve.
                    </div>
                  )}

                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Enviar Mensagem
                    <i className="ri-send-plane-line"></i>
                  </button>
                </form>
              </motion.div>

              {/* Info Sidebar */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
                className="lg:pt-14"
              >
                <div className="space-y-10">
                  {/* WhatsApp Card */}
                  <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                      <i className="ri-whatsapp-line text-xl"></i>
                    </div>
                    <h3 className="font-heading text-xl text-foreground-950 mb-2">WhatsApp</h3>
                    <p className="text-sm text-foreground-600 mb-4">A forma mais rápida de agendar sua consulta.</p>
                    <a
                      href={`https://wa.me/${clinicInfo.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-xs tracking-[0.06em] uppercase bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap"
                    >
                      (85) 9 8429-3412
                    </a>
                  </div>

                  {/* Phone Card */}
                  <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                      <i className="ri-phone-line text-xl"></i>
                    </div>
                    <h3 className="font-heading text-xl text-foreground-950 mb-2">Telefone</h3>
                    <p className="text-sm text-foreground-600 mb-4">Atendimento de segunda a sexta, 8h às 18h.</p>
                    <a
                      href={`tel:${clinicInfo.phone.replace(/\D/g, '')}`}
                      className="inline-flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
                    >
                      <i className="ri-phone-line"></i>
                      {clinicInfo.phone}
                    </a>
                  </div>

                  {/* Address Card */}
                  <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                      <i className="ri-map-pin-line text-xl"></i>
                    </div>
                    <h3 className="font-heading text-xl text-foreground-950 mb-2">Endereço</h3>
                    <div className="text-sm text-foreground-600 leading-relaxed space-y-1">
                      <p>{clinicInfo.address.street}</p>
                      <p>{clinicInfo.address.complement}</p>
                      <p>{clinicInfo.address.neighborhood} - {clinicInfo.address.city}/{clinicInfo.address.state}</p>
                      <p>CEP: {clinicInfo.address.zip}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-20 rounded-lg overflow-hidden aspect-[21/9]"
            >
              <iframe
                src={clinicInfo.googleMapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização Clínica Femme Vie"
                className="w-full h-full"
              ></iframe>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}